// Qué muestra `/alta-empresa/:plan` una vez que se sabe qué es esta cuenta: el ÚNICO
// traductor entre lo que se lee de la base (`resolveStepForUser`, en
// `pages/EnterpriseCheckout.tsx`: `profiles` + `company_profiles`) y lo que la pantalla
// aplica.
//
// Existe por un bug de plata. ADR-2026-08-18-alta-empresa-pais-solo-de-la-url hizo que
// el país cayera al que la cuenta YA tiene guardado cuando la URL no trae un `?pais=`
// usable, pero lo aplicó en un solo camino: el effect de montaje. El del login
// destructuraba `{ step, yaFueClientePago }` y TIRABA `paisGuardado` — y es justo el
// camino de quien ya tenía cuenta. Sin país, `paisFlags('').isArgentina` es `false`: a
// un argentino se le ofrecía Stripe en USD en vez del form de Rebill en ARS.
//
// Dónde duele: los links del CATÁLOGO (`/alta-empresa/pro`, `/alta-empresa/ultra-1`),
// que son los que eligen proveedor según el país. Un plan a medida no está afectado —
// cobra siempre por Stripe, en la moneda de su Price, sin importar el país.
//
// Por eso acá se devuelve TODO junto —paso, país e historial de pago— en un objeto: los
// caminos de la pantalla no eligen qué aplicar, aplican lo que sale de esta función. Un
// campo nuevo se agrega en un solo lugar y el único aplicador lo recibe solo.
// Ver ADR-2026-09-22-el-pais-del-alta-se-resuelve-en-un-solo-lugar.

import { resolverPaisCheckout } from './checkoutPlans';

/**
 * Lo que la pantalla averigua de la cuenta antes de decidir qué mostrar: la salida de
 * `resolveStepForUser`. El tipo vive acá —y no en la pantalla— para que la lectura y el
 * traductor no puedan desfasarse: sumarle un campo obliga a pasar por este archivo.
 */
export interface EstadoDeCuentaDelAlta {
  /** Paso crudo que resuelve la lectura de la base. `'error'` = no se pudo saber. */
  step: 'datos' | 'pago' | 'redirect' | 'error';
  /**
   * País que la cuenta ya tiene elegido (`company_profiles.pais`, con `profiles.pais`
   * de respaldo). `null` cuando no se pudo leer: no se inventa uno.
   */
  paisGuardado: string | null;
  /**
   * `paid_plan_start_date is not null` — la cuenta YA fue cliente pago alguna vez.
   * Sin esto, un ex cliente (que se dio de baja y quedó en `plan = 'gratis'`, o sea que
   * NO cae en `'redirect'`) llegaba al pago y veía el 30% que el servidor de pagos ya
   * no aplica: precio prometido ≠ precio cobrado.
   * Ver ADR-2026-08-19-alta-por-link-respeta-la-regla-del-cupon.
   */
  yaFueClientePago: boolean;
}

/**
 * Todo lo que la pantalla tiene que aplicar, junto. `paso` se llama distinto que el
 * `step` de arriba a propósito: son dos vocabularios (uno es el de la base, el otro el
 * de la pantalla) y que no se llamen igual evita pasar uno donde va el otro.
 *
 * `'redirect'` no es un paso que se pinte: es "esta cuenta ya paga, no la re-cobres,
 * mandala a la app".
 */
export interface PantallaDelAlta {
  paso: 'datos' | 'pago' | 'error-estado' | 'redirect';
  /** `''` = desconocido → precio en USD y cobro por Stripe, que es el fallback correcto. */
  pais: string;
  yaFueClientePago: boolean;
}

/**
 * País del PRIMER render, cuando todavía no se sabe nada de la cuenta: sólo la URL. Se
 * separa de `resolverPantallaDelAlta` porque en ese momento no hay país guardado que
 * consultar, no porque sea otra regla.
 */
export function paisInicialDelAlta(
  codigoDeLaUrl: string | null | undefined,
): string {
  return resolverPaisCheckout(codigoDeLaUrl, null);
}

/**
 * Traduce el estado de la cuenta a lo que muestra la pantalla.
 *
 * - El paso `'error'` se convierte en `'error-estado'`, la puerta que NO cobra cuando
 *   no se pudo leer el estado de la cuenta. Esa traducción estaba escrita a mano en
 *   cada camino, que es otra copia que se puede olvidar.
 * - El país sale de `resolverPaisCheckout`: el `?pais=` de la landing manda si es un
 *   código conocido y, si no, el que la cuenta ya eligió a mano. En los caminos de
 *   error `paisGuardado` es `null`, así que queda el de la URL — el mismo con el que se
 *   venía renderizando, y ahí no hay cobro que proteger porque esa pantalla no cobra.
 *   Lo que importa es que NO se invente un país.
 * - `yaFueClientePago` viaja tal cual: es lo que decide si se promete el cupón, y el
 *   valor que no promete nada es `true`.
 */
export function resolverPantallaDelAlta(
  estado: EstadoDeCuentaDelAlta,
  codigoDeLaUrl: string | null | undefined,
): PantallaDelAlta {
  return {
    paso: estado.step === 'error' ? 'error-estado' : estado.step,
    pais: resolverPaisCheckout(codigoDeLaUrl, estado.paisGuardado),
    yaFueClientePago: estado.yaFueClientePago,
  };
}

/**
 * El historial de pago que usa EL PRECIO QUE SE MUESTRA, que no siempre es el mismo que
 * el que decide el cobro.
 *
 * `yaFueClientePago` arranca en `true` —el valor que NO promete descuento— para fallar
 * cerrado en los pasos que cobran: ahí no se puede prometer lo que el payment server no
 * va a aplicar. Pero en "Creá tu cuenta" todavía no hay cuenta que consultar: no hay
 * usuario, no hay `paid_plan_start_date`, y quien está por registrarse es por definición
 * un alta nueva. Dejarlo en `true` ahí significaba pintar **precio de lista** en la
 * pantalla a la que la landing acababa de mandar con el 30% puesto, y bajarlo recién
 * después del signup: registrarse no puede ser el requisito para VER el precio prometido,
 * justo en el paso donde se cae la mayoría.
 *
 * Sólo en la solapa de REGISTRO: en la de login, quien entra puede ser un ex cliente, y
 * ahí el valor que no promete nada sigue siendo el correcto.
 *
 * Acotado al paso `'auth'` a propósito: el `couponCode` que viaja a Rebill se arma en el
 * paso `'pago'`, donde esta función devuelve `yaFueClientePago` tal cual. O sea que esto
 * **no puede** cambiar lo que se cobra — sólo lo que se muestra antes de que haya cuenta.
 * Ver ADR-2026-09-22-el-precio-del-paso-de-crear-cuenta-es-el-de-la-landing.
 */
export function exClienteParaElPrecio(
  paso: string,
  modoDeAuth: 'signup' | 'signin',
  yaFueClientePago: boolean,
): boolean {
  return paso === 'auth' && modoDeAuth === 'signup' ? false : yaFueClientePago;
}

/**
 * A dónde tiene que volver el alta después de Google.
 *
 * El OAuth se va del documento y vuelve, así que la URL de retorno es lo ÚNICO que
 * sobrevive: si se pierde, se pierden con ella el plan (`/alta-empresa/<slug>`), el
 * `?pais=` —que decide si se cobra en ARS por Rebill o en USD por Stripe—, el `?sid=`
 * que linkea el embudo, el `?src=` y el `?desc=0` de los links de ventas. Por eso se
 * vuelve a la MISMA url, con su query entera, y no a una ruta fija como hacen las otras
 * pantallas de auth (que no tienen nada que conservar).
 *
 * Es pura para poder testear justamente eso: que la query no se caiga.
 * Ver ADR-2026-09-22-google-en-el-alta-por-link.
 */
export function urlDeVueltaDelAlta(
  origin: string,
  pathname: string,
  search: string,
): string {
  return `${origin}${pathname}${search}`;
}
